import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getAdminAccounts, createAdminAccount, updateAdminAccount,
  resetAdminAccountPass, deactivateAdminAccount,
} from "../../../api/apiServices";
import { useAuth } from "../../../context/useAuth";

const ROLES = [
  {
    value: "super_admin",
    label: "Super Admin",
    desc:  "Full access including managing other admins",
    color: "#ef4444",
    icon:  "ri-shield-star-line",
  },
  {
    value: "admin",
    label: "Admin",
    desc:  "Full platform management except admin accounts",
    color: "#00C896",
    icon:  "ri-admin-line",
  },
  {
    value: "support",
    label: "Support",
    desc:  "KYC review and alerts only",
    color: "#f59e0b",
    icon:  "ri-customer-service-2-line",
  },
];

const ALL_PERMISSIONS = [
  { key: "manage_admins",       label: "Manage Admins",       group: "Admin" },
  { key: "manage_users",        label: "Manage Users",        group: "Users" },
  { key: "manage_kyc",          label: "Review KYC",          group: "Users" },
  { key: "manage_transactions", label: "Manage Transactions", group: "Finance" },
  { key: "manage_wallets",      label: "Manage Wallets",      group: "Finance" },
  { key: "manage_assets",       label: "Manage Assets",       group: "Finance" },
  { key: "manage_services",     label: "Manage Services",     group: "Platform" },
  { key: "manage_settings",     label: "Manage Settings",     group: "Platform" },
  { key: "manage_marketing",    label: "Marketing",           group: "Platform" },
  { key: "view_analytics",      label: "View Analytics",      group: "Reports" },
  { key: "view_alerts",         label: "View Alerts",         group: "Reports" },
  { key: "dismiss_alerts",      label: "Dismiss Alerts",      group: "Reports" },
];

const inputCls = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 transition-colors";
const labelCls = "text-white/40 text-[10px] uppercase tracking-wide block mb-1.5 font-semibold";

// ── Confirm modal ─────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, confirmColor = "red", loading, onConfirm, onCancel }) {
  const btnCls = { red: "bg-red-600 hover:bg-red-700 text-white", green: "bg-[#00C896] hover:bg-[#00b386] text-black" }[confirmColor];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${btnCls}`}>
            {loading ? <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : null}
            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Admin form modal ─────────────────────────────────────────
function AdminModal({ mode, admin, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstName:   admin?.firstName   || "",
    lastName:    admin?.lastName    || "",
    email:       admin?.email       || "",
    phone:       admin?.phone       || "",
    password:    "",
    adminRole:   admin?.adminRole   || "admin",
    permissions: admin?.adminPermissions || [],
  });
  const [saving,       setSaving]       = useState(false);
  const [tab,          setTab]          = useState("basic"); // "basic" | "permissions"
  const [showPassword, setShowPassword] = useState(false);

  const ROLE_DEFAULTS = {
    super_admin: ALL_PERMISSIONS.map(p => p.key),
    admin:       ALL_PERMISSIONS.filter(p => p.key !== "manage_admins").map(p => p.key),
    support:     ["manage_kyc", "view_alerts"],
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRoleChange = (role) => {
    setForm(p => ({ ...p, adminRole: role, permissions: ROLE_DEFAULTS[role] || [] }));
  };

  const togglePermission = (perm) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(perm)
        ? p.permissions.filter(x => x !== perm)
        : [...p.permissions, perm],
    }));
  };

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (mode === "add" && form.password.length < 8)   { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const payload = {
        firstName:         form.firstName.trim(),
        lastName:          form.lastName.trim(),
        email:             form.email.trim(),
        phone:             form.phone.trim(),
        adminRole:         form.adminRole,
        customPermissions: form.permissions,
        ...(mode === "add" && { password: form.password }),
      };
      if (mode === "add") await createAdminAccount(payload);
      else               await updateAdminAccount(admin.userId, payload);
      toast.success(mode === "add" ? "Admin account created" : "Admin updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const permGroups = ALL_PERMISSIONS.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70">
      <div className="w-full sm:max-w-lg bg-[#161616] border border-white/8 rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">

        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <p className="text-white font-bold text-sm">{mode === "add" ? "Create Admin Account" : `Edit — ${admin.firstName}`}</p>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/60" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 px-6 pt-4 pb-0 flex-shrink-0">
          {[{ id: "basic", label: "Details" }, { id: "permissions", label: "Permissions" }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                tab === t.id ? "bg-[#00C896] text-black" : "text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {tab === "basic" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>First Name</label><input value={form.firstName} onChange={e => set("firstName", e.target.value)} placeholder="Ada" className={inputCls} /></div>
                <div><label className={labelCls}>Last Name</label><input value={form.lastName} onChange={e => set("lastName", e.target.value)} placeholder="Lovelace" className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="ada@easwallet.app" className={inputCls} disabled={mode === "edit"} /></div>
              <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+2348012345678" className={inputCls} /></div>
              {mode === "add" && (
                <div>
                  <label className={labelCls}>Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={e => set("password", e.target.value)}
                      placeholder="Min. 8 characters"
                      className={inputCls + " pr-10"}
                    />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60 transition-colors">
                      <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Role selector */}
              <div>
                <label className={labelCls}>Role</label>
                <div className="space-y-2">
                  {ROLES.filter(r => r.value !== "super_admin").map(r => (
                    <button key={r.value} onClick={() => handleRoleChange(r.value)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        form.adminRole === r.value ? "border-[#00C896] bg-[#00C896]/10" : "border-white/8 bg-white/4 hover:bg-white/8"
                      }`}>
                      <i className={`${r.icon} text-lg`} style={{ color: form.adminRole === r.value ? "#00C896" : "#ffffff40" }} />
                      <div>
                        <p className={`text-sm font-bold ${form.adminRole === r.value ? "text-[#00C896]" : "text-white/60"}`}>{r.label}</p>
                        <p className="text-white/30 text-[10px]">{r.desc}</p>
                      </div>
                      {form.adminRole === r.value && <i className="ri-check-line text-[#00C896] ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {tab === "permissions" && (
            <div className="space-y-5">
              <p className="text-white/40 text-xs">Customise individual permissions. Changes override the role defaults.</p>
              {Object.entries(permGroups).map(([group, perms]) => (
                <div key={group}>
                  <p className="text-white/30 text-[10px] uppercase tracking-wide font-bold mb-2">{group}</p>
                  <div className="space-y-1.5">
                    {perms.map(p => {
                      const on = form.permissions.includes(p.key);
                      return (
                        <button key={p.key} onClick={() => togglePermission(p.key)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                            on ? "bg-[#00C896]/10 border border-[#00C896]/20" : "bg-white/4 border border-white/5 hover:bg-white/8"
                          }`}>
                          <span className={`text-xs font-semibold ${on ? "text-[#00C896]" : "text-white/40"}`}>{p.label}</span>
                          <div className={`w-4 h-4 rounded flex items-center justify-center ${on ? "bg-[#00C896]" : "bg-white/10"}`}>
                            {on && <i className="ri-check-line text-[10px] text-black font-bold" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 pb-6 pt-4 border-t border-white/5 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] hover:bg-[#00b386] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" /> : null}
            {saving ? "Saving…" : mode === "add" ? "Create Account" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Password reset modal ──────────────────────────────────────
function ResetPasswordModal({ admin, onClose }) {
  const [pwd,          setPwd]          = useState("");
  const [saving,       setSaving]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleReset = async () => {
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await resetAdminAccountPass(admin.userId, pwd);
      toast.success("Password reset successfully");
      onClose();
    } catch { toast.error("Failed to reset password"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4">
          <p className="text-white font-bold mb-1">Reset Password</p>
          <p className="text-white/40 text-xs mb-4">For {admin.firstName} {admin.lastName} ({admin.email})</p>
          <label className={labelCls}>New Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              placeholder="Min. 8 characters"
              className={inputCls + " pr-10"}
            />
            <button type="button" onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60 transition-colors">
              <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
            </button>
          </div>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold">Cancel</button>
          <button onClick={handleReset} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-black text-sm font-bold disabled:opacity-50">
            {saving ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function AdminAccountsView() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.adminRole === "super_admin";

  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);   // null | { mode, admin? }
  const [pwdModal, setPwdModal] = useState(null); // admin object
  const [confirm, setConfirm] = useState(null);   // { admin }
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminAccounts();
      if (res.status) setAdmins(res.data);
    } catch { toast.error("Failed to load admin accounts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async () => {
    setWorking(true);
    try {
      await deactivateAdminAccount(confirm.admin.userId);
      setAdmins(prev => prev.map(a => a.userId === confirm.admin.userId ? { ...a, isActive: false } : a));
      setConfirm(null);
      toast.success("Account deactivated");
    } catch { toast.error("Failed to deactivate"); }
    finally { setWorking(false); }
  };

  const handleReactivate = async (admin) => {
    try {
      await updateAdminAccount(admin.userId, { isActive: true });
      setAdmins(prev => prev.map(a => a.userId === admin.userId ? { ...a, isActive: true } : a));
      toast.success("Account reactivated");
    } catch { toast.error("Failed to reactivate"); }
  };

  const getRoleConfig = (role) => ROLES.find(r => r.value === role) || ROLES[1];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Admin Accounts</h2>
          <p className="text-white/40 text-xs mt-0.5">{admins.length} team member{admins.length !== 1 ? "s" : ""}</p>
        </div>
        {isSuperAdmin && (
          <button onClick={() => setModal({ mode: "add" })}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896] hover:bg-[#00b386] text-black text-xs font-bold rounded-xl transition-colors">
            <i className="ri-user-add-line" />Create Admin
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl px-4 py-3">
          <i className="ri-lock-line text-amber-400" />
          <p className="text-amber-400 text-xs">Only super admins can create or modify admin accounts.</p>
        </div>
      )}

      {/* Admin cards */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-[#161616] rounded-2xl border border-white/5 animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {admins.map(admin => {
            const role       = getRoleConfig(admin.adminRole);
            const isSelf     = admin.userId === currentUser?.userId;
            const isInactive = admin.isActive === false;

            return (
              <div key={admin.userId}
                className={`bg-[#161616] border rounded-2xl p-4 transition-all ${isInactive ? "border-white/5 opacity-50" : "border-white/5"}`}>
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 text-white font-bold text-base"
                    style={{ background: role.color + "20", color: role.color }}>
                    {admin.firstName?.charAt(0)}{admin.lastName?.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-white font-semibold text-sm">
                        {admin.firstName} {admin.lastName}
                        {isSelf && <span className="text-white/30 text-xs ml-1">(you)</span>}
                      </p>
                      <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: role.color + "20", color: role.color }}>
                        <i className={`${role.icon} text-[11px]`} />
                        {role.label}
                      </span>
                      {isInactive && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/30">Inactive</span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs">{admin.email}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(admin.adminPermissions || []).slice(0, 5).map(p => (
                        <span key={p} className="text-[9px] px-1.5 py-0.5 rounded-md bg-white/5 text-white/25">
                          {p.replace(/_/g, " ")}
                        </span>
                      ))}
                      {(admin.adminPermissions || []).length > 5 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-white/5 text-white/20">
                          +{admin.adminPermissions.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions — super admin only, and not on self for destructive ops */}
                  {isSuperAdmin && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => setModal({ mode: "edit", admin })}
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button onClick={() => setPwdModal(admin)}
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors"
                        title="Reset password">
                        <i className="ri-key-line text-sm" />
                      </button>
                      {!isSelf && (
                        isInactive ? (
                          <button onClick={() => handleReactivate(admin)}
                            className="w-8 h-8 rounded-lg bg-[#00C896]/10 hover:bg-[#00C896]/20 flex items-center justify-center text-[#00C896] transition-colors"
                            title="Reactivate">
                            <i className="ri-user-received-line text-sm" />
                          </button>
                        ) : (
                          <button onClick={() => setConfirm({ admin })}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"
                            title="Deactivate">
                            <i className="ri-user-unfollow-line text-sm" />
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {modal && <AdminModal mode={modal.mode} admin={modal.admin} onClose={() => setModal(null)} onSaved={load} />}
      {pwdModal && <ResetPasswordModal admin={pwdModal} onClose={() => setPwdModal(null)} />}
      {confirm && (
        <ConfirmModal
          title="Deactivate Admin"
          message={`Deactivate ${confirm.admin.firstName}'s account? They will lose all admin access immediately.`}
          confirmLabel="Deactivate"
          confirmColor="red"
          loading={working}
          onConfirm={handleDeactivate}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
